package com.tovtalk.ui.screen.chat

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.ChatThread
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.chat.ChatListViewModel
import com.tovtalk.ui.components.TovTalkAvatar
import com.tovtalk.ui.components.TovTalkEmptyState
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@Composable
fun ChatListScreen(
    onNavigateToChat: (Int, String) -> Unit,
    onNavigateBack: (() -> Unit)? = null,
    onChatSelected: ((Int, String) -> Unit)? = null,
    selectedChatId: Int? = null,
    onNavigateToStories: ((Int) -> Unit)? = null,
    viewModel: ChatListViewModel = koinInject()
) {
    val chatThreadsState by viewModel.chatThreads.collectAsState()

    DisposableEffect(Unit) {
        onDispose { viewModel.onClear() }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.backgroundPrimary)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
        ) {
            onNavigateBack?.let { onBack ->
                Row(
                    Modifier
                        .fillMaxWidth()
                        .padding(TovTalkTheme.spacing.xs),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = null)
                    }
                    Text(
                        StringProvider.get("nav_messages"),
                        style = TovTalkTheme.typography.titleLarge
                    )
                }
            }
            when (val state = chatThreadsState) {
                is UiState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = TovTalkTheme.colors.brand)
                    }
                }
                is UiState.Error -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = state.message,
                            color = TovTalkTheme.colors.error
                        )
                    }
                }
                is UiState.Success -> {
                    val list = (state as UiState.Success<List<ChatThread>>).data
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(bottom = TovTalkTheme.spacing.md)
                    ) {
                        if (list.isEmpty()) {
                            item {
                                TovTalkEmptyState(
                                    message = StringProvider.get("chat_empty_inbox"),
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(400.dp)
                                )
                            }
                        } else {
                            items(list, key = { it.otherUser.id }) { thread ->
                                ModernChatThreadItem(
                                    thread = thread,
                                    isSelected = selectedChatId == thread.otherUser.id,
                                    onClick = {
                                        if (onChatSelected != null) {
                                            onChatSelected(thread.otherUser.id, thread.otherUser.fullname)
                                        } else {
                                            onNavigateToChat(thread.otherUser.id, thread.otherUser.fullname)
                                        }
                                    },
                                    onStoryClick = if (thread.otherUser.hasActiveStory && onNavigateToStories != null) { { onNavigateToStories(thread.otherUser.id) } } else null
                                )
                            }
                        }
                    }
                }
                else -> {}
            }
        }
    }
}

@Composable
private fun ModernChatThreadItem(
    thread: ChatThread,
    isSelected: Boolean = false,
    onClick: () -> Unit,
    onStoryClick: (() -> Unit)? = null
) {
    val hasUnread = thread.unreadCount > 0
    val backgroundColor by animateColorAsState(
        targetValue = when {
            isSelected -> TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.subtle)
            hasUnread -> TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.faintest)
            else -> androidx.compose.ui.graphics.Color.Transparent
        },
        label = "threadBg"
    )
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(backgroundColor)
            .then(
                if (isSelected) Modifier.border(
                    width = 1.dp,
                    color = TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.medium),
                    shape = RoundedCornerShape(TovTalkTheme.radius.sm)
                ) else Modifier
            )
            .clickable(onClick = onClick)
            .padding(
                horizontal = TovTalkTheme.spacing.md,
                vertical = TovTalkTheme.spacing.sm
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TovTalkAvatar(
            imageUrl = thread.otherUser.profilePhoto,
            size = TovTalkTheme.sizes.avatarLg,
            hasStory = thread.otherUser.hasActiveStory && onStoryClick != null,
            onStoryClick = onStoryClick
        )
        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
        Column(modifier = Modifier.weight(1f)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = thread.otherUser.fullname,
                    style = TovTalkTheme.typography.titleSmall,
                    color = TovTalkTheme.colors.textPrimary,
                    fontWeight = if (hasUnread) FontWeight.Bold else FontWeight.Normal
                )
                thread.lastMessage?.let { lastMsg ->
                    Text(
                        text = formatMessageTime(lastMsg.createdAt),
                        style = TovTalkTheme.typography.caption,
                        color = if (hasUnread) TovTalkTheme.colors.brand else TovTalkTheme.colors.textTertiary
                    )
                }
            }
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xxxs))
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                thread.lastMessage?.let { lastMsg ->
                    Text(
                        text = lastMsg.content ?: "",
                        style = TovTalkTheme.typography.bodySmall,
                        color = if (hasUnread) TovTalkTheme.colors.textPrimary else TovTalkTheme.colors.textSecondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        fontWeight = if (hasUnread) FontWeight.Medium else FontWeight.Normal,
                        modifier = Modifier.weight(1f)
                    )
                }
                if (hasUnread) {
                    Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xs))
                    Box(
                        modifier = Modifier
                            .clip(CircleShape)
                            .background(TovTalkTheme.colors.brand)
                            .padding(horizontal = TovTalkTheme.spacing.xs, vertical = TovTalkTheme.spacing.xxxs),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = if (thread.unreadCount > 99) "99+" else thread.unreadCount.toString(),
                            style = TovTalkTheme.typography.labelSmall,
                            color = TovTalkTheme.colors.onBrand
                        )
                    }
                }
            }
        }
    }
}

private fun formatMessageTime(instant: Instant): String {
    val now = com.tovtalk.utils.nowInstant()
    val diffMs = now.toEpochMilliseconds() - instant.toEpochMilliseconds()
    val diffMin = diffMs / 60_000
    val diffHours = diffMs / 3600_000
    val diffDays = diffMs / 86400_000
    return when {
        diffMin < 1 -> "now"
        diffMin < 60 -> "${diffMin}m"
        diffHours < 24 -> "${diffHours}h"
        diffDays < 7 -> "${diffDays}d"
        else -> {
            val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
            "${localDateTime.day}/${localDateTime.month.ordinal + 1}"
        }
    }
}
